/**
 * Chart notes slice. A note is a little post-it we drop on the chart to come
 * back to: a hazard we've spotted, a good anchoring spot, a reminder for the
 * pilotage. Kept in its own small store, like marks and tracks, because notes
 * have their own life cycle (place, edit, delete) and bundling them onto the
 * main chart-display state would just be noise.
 *
 * The whole point of this slice is that a note is REAL and SURVIVES a restart.
 * Android kills a backgrounded app hard, so we mirror the marks persistence in
 * ./persist: load the saved slice on boot, write it back on every change. A note
 * the skipper dropped before lunch is still there after.
 */
import { create } from 'zustand';
import { loadJson, saveJson } from './persist';

/** A post-it note pinned to a point on the chart. */
export interface ChartNote {
  id: string;
  /** [lon, lat] of the note's pin. */
  position: [number, number];
  /** Short heading shown as the icon's label, e.g. "Pot buoys". Optional. */
  title?: string;
  /** The body text of the note. May be empty right after placing. */
  text: string;
  createdMs: number;
  updatedMs: number;
}

interface NotesState {
  notes: ChartNote[];
  addNote: (note: ChartNote) => void;
  updateNote: (id: string, patch: Partial<Pick<ChartNote, 'position' | 'title' | 'text'>>) => void;
  removeNote: (id: string) => void;
}

// Note ids are their own counter (note-1, note-2, ...), seeded past any restored
// note on boot so a fresh note never reuses an id that's already on the chart.
let nextNoteNum = 1;
export function nextNoteId(): string {
  return `note-${nextNoteNum++}`;
}

export const useNotesStore = create<NotesState>((set) => ({
  notes: [],
  addNote: (note) => set((s) => ({ notes: [...s.notes, note] })),
  updateNote: (id, patch) =>
    set((s) => ({
      notes: s.notes.map((n) => (n.id === id ? { ...n, ...patch, updatedMs: Date.now() } : n)),
    })),
  removeNote: (id) => set((s) => ({ notes: s.notes.filter((n) => n.id !== id) })),
}));

/* --- Persistence ---------------------------------------------------------- */
const NOTES_LS = 'acp.notes.v1';

interface PersistedNotes {
  notes: ChartNote[];
}

/** Pull the number out of a `note-12` id, or 0 if it doesn't fit the shape. */
function idNum(id: string): number {
  if (typeof id !== 'string' || !id.startsWith('note-')) return 0;
  const n = Number(id.slice('note-'.length));
  return Number.isFinite(n) ? n : 0;
}

/** Jump the id counter past every restored note, so the next id is fresh. */
function seedNoteCounter(notes: ChartNote[]): void {
  let max = 0;
  for (const n of notes) max = Math.max(max, idNum(n.id));
  nextNoteNum = Math.max(nextNoteNum, max + 1);
}

/** A restored note might be missing fields (old shape); keep only valid ones. */
function isValidNote(n: unknown): n is ChartNote {
  if (!n || typeof n !== 'object') return false;
  const c = n as Record<string, unknown>;
  return (
    typeof c.id === 'string' &&
    Array.isArray(c.position) &&
    c.position.length === 2 &&
    typeof c.position[0] === 'number' &&
    typeof c.position[1] === 'number'
  );
}

/** Read the saved notes slice back into the store. Runs once on boot. */
export function hydrateNotes(): void {
  const p = loadJson<PersistedNotes>(NOTES_LS, { notes: [] });
  const raw = Array.isArray(p.notes) ? p.notes : [];
  const notes: ChartNote[] = raw.filter(isValidNote).map((n) => ({
    id: n.id,
    position: [n.position[0], n.position[1]],
    title: typeof n.title === 'string' ? n.title : undefined,
    text: typeof n.text === 'string' ? n.text : '',
    createdMs: typeof n.createdMs === 'number' ? n.createdMs : Date.now(),
    updatedMs: typeof n.updatedMs === 'number' ? n.updatedMs : Date.now(),
  }));
  seedNoteCounter(notes);
  useNotesStore.setState({ notes });
}

// Hydrate BEFORE listening, so restoring the saved slice on boot doesn't write
// it straight back. After that, persist on any change.
hydrateNotes();
useNotesStore.subscribe((s) => saveJson(NOTES_LS, { notes: s.notes }));
